



































import { computed, defineComponent, PropType, ref, toRef, watch } from "@vue/composition-api";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAdditionalProviders from "@/hooks/useAdditionalProviders";
import useAuthUser from "@/hooks/useAuthUser";
import { ProviderPrefix } from "@/lib/api";
import { formatMoneyString, getMainCurrencyCode } from "@/lib/moneyHelpers";
import config from "@/config";
import AttachmentsInfo from "@/components/AttachmentsInfo";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import LinkifiedText from "@/components/LinkifiedText";
import MessageMediaGallery from "@/components/MessageItem/views/MessageItem/MessageMediaGallery.vue";

export default defineComponent({
  name: "MessagePreviewModal",
  components: { MessageMediaGallery, AttachmentsInfo, LinkifiedText, BaseConfirm, BaseButton },
  props: {
    paymentPackages: { type: Array as PropType<PaymentPackage[]>, required: true },
    message: { type: Object as PropType<Message> },
    opponentId: { type: String, required: true },
  },
  setup(props) {
    const { providers, getTokens } = useAdditionalProviders(ProviderPrefix.ADMIN);
    const { authUserStrict } = useAuthUser();

    const activeMessage = ref<Message | null>(null);

    const linksValidation = computed(
      () => config.featureClickableUserLinks && (props.message?.senderUser as UserPublicData)?.verified
    );
    const isAllowExternalLinks = computed(() => config.featureClickableUserExternalLinks);

    const isAuthor = computed(() => props.message?.senderId === authUserStrict.value?.id);
    const hasPrice = computed(() => props.message?.attachmentPricePackageId !== "0");
    const available = computed(() => {
      if (!isAuthor.value) {
        return props.message?.attachmentAvailable ?? false;
      }
      const purchased = !!props.message?.attachmentPurchasers?.includes(props.opponentId);
      return !hasPrice.value || purchased;
    });
    const amount = computed(
      () =>
        props.paymentPackages
          .find(pkg => pkg.id === props.message?.attachmentPricePackageId)
          ?.costs.find(cost => cost.currency === getMainCurrencyCode())?.cost || 0
    );
    const price = computed(() => formatMoneyString(amount.value, { currencyCode: getMainCurrencyCode() }));
    const attachments = computed(() => activeMessage.value?.attachment ?? props.message?.attachment ?? []);

    const getUpdatedAttachments = async (messageId: string) => {
      if (!providers.value) {
        await getTokens();
        if (!providers.value) return null;
      }

      const response = await providers.value.providers.MessagesProvider.find({ data: { ids: [messageId] } });
      return response.result[0] || null;
    };

    watch(toRef(props, "message"), async message => {
      if (message && !isAuthor.value && !message.attachmentAvailable) {
        activeMessage.value = await getUpdatedAttachments(message.id);
      } else {
        activeMessage.value = message ?? null;
      }
    });

    return {
      authUserStrict,
      linksValidation,
      isAllowExternalLinks,
      available,
      price,
      amount,
      attachments,
    };
  },
});
